import React, { useState, useMemo } from 'react';
import { Dialog, Input, Button, Tooltip, MessagePlugin } from 'tdesign-react';
import { Form } from '../../../ui/universe';

/**
 * NewItemNames 字段输入组件
 * 支持文本字段（KeyType=0）和表格字段（KeyType=1）两组，
 * 每组下方有「+ 文本key / + 表格key」按钮，点击按钮弹出 Dialog 输入字段名
 * 添加的字段以纯展示 tag 形式呈现（带 × 删除按钮）
 */
function NewItemNamesField({ params, value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  const [textDialogVisible, setTextDialogVisible] = useState(false);
  const [tableDialogVisible, setTableDialogVisible] = useState(false);
  const [inputName, setInputName] = useState('');

  const textFields = useMemo(() => items.filter((i) => i.KeyType === 0), [items]);
  const tableFields = useMemo(() => items.filter((i) => i.KeyType === 1), [items]);

  const openAddDialog = (keyType) => {
    setInputName('');
    if (keyType === 0) setTextDialogVisible(true);
    else setTableDialogVisible(true);
  };

  const closeAddDialog = () => {
    setTextDialogVisible(false);
    setTableDialogVisible(false);
    setInputName('');
  };

  const handleAddConfirm = (keyType) => {
    const name = (inputName || '').trim();
    if (!name) {
      MessagePlugin.warning('请输入字段名称');
      return;
    }
    if (items.some((i) => i.KeyName === name && i.KeyType === keyType)) {
      MessagePlugin.warning('字段名称已存在');
      return;
    }
    const newItem = {
      KeyName: name,
      KeyType: keyType,
    };
    onChange([...items, newItem]);
    closeAddDialog();
  };

  const handleRemove = (keyType, keyName) => {
    onChange(items.filter((i) => !(i.KeyType === keyType && i.KeyName === keyName)));
  };

  const renderFieldGroup = (keyType, label, desc, fields) => {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: '14px', color: '#333', fontWeight: 500 }}>{label}</span>
          <Tooltip content={desc} theme="light">
            <span
              className="ocrDemo-icon-question"
              style={{ marginLeft: 4, cursor: 'help', color: '#999' }}
            ></span>
          </Tooltip>
        </div>
        <div
          style={{
            border: '1px solid #dcdfe6',
            borderRadius: 6,
            padding: '8px 12px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            background: '#fff',
            minHeight: 36,
          }}
        >
          {fields.length === 0 ? (
            <span style={{ color: '#c0c4cc', fontSize: 13 }}>
              {keyType === 0
                ? '点击下方「+ 文本key」添加文本字段'
                : '点击「+ 表格key」添加表格列字段'}
            </span>
          ) : (
            fields.map((field) => (
              <div
                key={`${keyType}-${field.KeyName}`}
                className="ocrDemo-tag-container primary"
                style={{ marginTop: 0 }}
              >
                <div className="ocrDemo-tag__border"></div>
                <div className="ocrDemo-tag__bg"></div>
                <span className="ocrDemo-tag__content">{field.KeyName}</span>
                <span
                  className="ocrDemo-tag__close"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(keyType, field.KeyName);
                  }}
                ></span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderFieldGroup(
        0,
        '文本字段',
        '文本类（KV）字段；点击下方按钮添加',
        textFields
      )}
      {renderFieldGroup(
        1,
        '表格字段',
        '表格类字段，用于抽取表格中的列',
        tableFields
      )}
      <div style={{ marginTop: 4, display: 'flex', gap: 8 }}>
        <Button
          theme="default"
          variant="outline"
          size="small"
          onClick={() => openAddDialog(0)}
        >
          + 文本key
        </Button>
        <Button
          theme="default"
          variant="outline"
          size="small"
          onClick={() => openAddDialog(1)}
        >
          + 表格key
        </Button>
      </div>

      {/* 文本字段添加弹窗 */}
      <Dialog
        visible={textDialogVisible}
        onClose={closeAddDialog}
        header="添加自定义文本字段"
        width={420}
        onCancel={closeAddDialog}
        onConfirm={() => handleAddConfirm(0)}
        confirmBtn="确定"
        cancelBtn="取消"
      >
        <Form>
          <Form.Item label="字段名称">
            <Input
              value={inputName}
              onChange={(v) => setInputName(v)}
              placeholder="请输入字段名称，如：发票号码"
              maxlength={64}
              autofocus
            />
          </Form.Item>
        </Form>
      </Dialog>

      {/* 表格字段添加弹窗 */}
      <Dialog
        visible={tableDialogVisible}
        onClose={closeAddDialog}
        header="添加自定义表格字段"
        width={420}
        onCancel={closeAddDialog}
        onConfirm={() => handleAddConfirm(1)}
        confirmBtn="确定"
        cancelBtn="取消"
      >
        <Form>
          <Form.Item label="字段名称">
            <Input
              value={inputName}
              onChange={(v) => setInputName(v)}
              placeholder="请输入表格列字段名称，如：商品名称"
              maxlength={64}
              autofocus
            />
          </Form.Item>
        </Form>
      </Dialog>
    </div>
  );
}

export default NewItemNamesField;
